import { Component, OnInit, OnDestroy, signal, inject, computed, ViewChild, ElementRef } from '@angular/core';
import { Router } from '@angular/router';
import { Title } from '@angular/platform-browser';
import { CommonModule } from '@angular/common';
import { MessageService, MenuItem } from 'primeng/api';
import { Toast } from 'primeng/toast';
import { BreadcrumbModule } from 'primeng/breadcrumb';
import { ButtonModule } from 'primeng/button';
import { ChartModule } from 'primeng/chart';
import { ColumnConfig, TABLE_CONFIG } from './table-config';
import { RegistryTableComponent } from '../../../core/registry-table/registry-table.component';
import { ProfessionalsService } from '../../../core/services/professionals.service';
import { FormDropdownService } from '../../../core/services/form-dropdown.service';
import { ContractRegistryService } from '../../services/contract-registry.service';
import { Subject } from 'rxjs';
import { debounceTime, takeUntil } from 'rxjs/operators';
import { 
  ProfessionalSummaryResponse, 
  ProfessionalsListFilters 
} from '../../../core/models/professional.models';

interface StatusCard {
  label: string;
  value: number;
  status: string;
  styleClass: string;
}

interface CategoryCard {
  category: string;
  label: string;
  icon: string;
  colorClass: string;
  buttonClass: string;
  active: number;
  inProgress: number;
  waitingResponse: number;
  contratti: number;
}

@Component({
  selector: 'app-dashboard',
  standalone: true,
  providers: [MessageService],
  imports: [
    CommonModule,
    Toast,
    RegistryTableComponent,
    BreadcrumbModule,
    ButtonModule,
    ChartModule,
  ],
  templateUrl: './dashboard.html',
  styleUrl: './dashboard.scss',
})
export class Dashboard implements OnInit, OnDestroy {
  private messageService = inject(MessageService);
  private router = inject(Router);
  private professionalsService = inject(ProfessionalsService);
  private formDropdownService = inject(FormDropdownService);
  private contractRegistryService = inject(ContractRegistryService);
  private titleService = inject(Title);
  private filterSubject = new Subject<Record<string, any>>();
  private destroy$ = new Subject<void>();
  private currentTableFilters: Record<string, any> = { professionalStatus: ['IN_PROGRESS'] };

  // Constants
  private readonly EMPTY_VALUE = '-';
  private readonly DEFAULT_DASHBOARD_FILTERS: ProfessionalsListFilters = {
    professionalStatus: ['IN_PROGRESS'],
  };

  // Table configuration
  tableTitle = 'Ultime attività di registrazione fiduciari';
  tableIcon = 'pi-users';
  tableEmptyStateMessage = 'Non ci sono attività di registrazione in attesa di un\'azione AXA.';
  columns: ColumnConfig[] = [];
  data = signal<any[]>([]);
  allData = signal<any[]>([]);
  loading = signal<boolean>(false);
  tableInitialFilters = signal<Record<string, any>>({ professionalStatus: ['IN_PROGRESS'] });
  breadcrumbItems = signal<MenuItem[]>([{ label: 'Dashboard' }]);

  // Dashboard statistics
  statusCards = signal<StatusCard[]>([]);
  categoryCards = signal<CategoryCard[]>([]);
  private activeContractCounts = signal<Record<string, number>>({});

  // Chart data
  distributionChartData = signal<any>(null);
  distributionChartOptions = signal<any>(null);
  registrationsChartData = signal<any>(null);
  registrationsChartOptions = signal<any>(null);

  // Reference to table section for scrolling
  @ViewChild('registryTableSection', { read: ElementRef }) registryTableSection?: ElementRef;

  ngOnInit(): void {
    // WCAG 2.4.2: Set descriptive page title
    this.titleService.setTitle('Dashboard Fiduciari - Portale Fiduciari AXA');
    
    this.loadConfiguration();
    this.formDropdownService.preloadTrusteeCategories().subscribe();
    this.filterSubject.pipe(debounceTime(700), takeUntil(this.destroy$)).subscribe((tableFilters) => {
      this.loadTrustees(this.mapTableFiltersToBeFilters(tableFilters));
    });
    this.loadTrustees();
    this.loadDashboardCards();
    this.loadActiveContractCounts();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Loads table configuration from TABLE_CONFIG
   */
  private loadConfiguration(): void {
    const config = TABLE_CONFIG['elencoFiduciari'];
    if (config) {
      this.columns = config.columns;
    }
  }

  /**
   * Calculates statistics from dashboard cards API data
   */
  private calculateStatisticsFromCards(cardData: any[]): void {
    // Calculate active assets across all categories for the top status card.
    const totalActive = cardData.reduce(
      (sum, card) => sum + (card.category === 'NK_BODYSHOP' ? 0 : card.activeAssets || 0),
      0,
    );
    const totalInProgress = cardData.reduce((sum, card) => sum + (card.assetsActive || 0), 0);
    const totalWaiting = cardData.reduce(
      (sum, card) => sum + (card.assetsAwaitingResponse || 0),
      0,
    );
    const totalContratti = this.getTotalActiveContracts();

    this.statusCards.set([
      {
        label: 'Totali Attivi',
        value: totalActive,
        status: 'ACTIVE',
        styleClass: 'status-active',
      },
      {
        label: 'In Lavorazione',
        value: totalInProgress,
        status: 'IN_LAVORAZIONE',
        styleClass: 'status-in-progress',
      },
      {
        label: 'In Attesa di Risposta',
        value: totalWaiting,
        status: 'IN_ATTESA_DI_RISPOSTA',
        styleClass: 'status-waiting',
      },
      {
        label: 'Contratti',
        value: totalContratti,
        status: 'CONTRATTI',
        styleClass: 'status-contratti',
      },
    ]);

    // Build category cards from API data
    const categoryMap = new Map<string, any>();
    const categories = [
      {
        category: 'AUTO_EXPERT',
        label: 'Perito Auto',
        icon: 'pi-car',
        colorClass: 'category-perito-auto',
        buttonClass: 'btn-perito-auto',
      },
      {
        category: 'RE_EXPERT',
        label: 'Perito RE',
        icon: 'pi-building',
        colorClass: 'category-perito-re',
        buttonClass: 'btn-perito-re',
      },
      {
        category: 'INVESTIGATOR',
        label: 'Investigatore',
        icon: 'pi-search',
        colorClass: 'category-investigatore',
        buttonClass: 'btn-investigatore',
      },
      {
        category: 'DOCTOR',
        label: 'Medico',
        icon: 'pi-heart',
        colorClass: 'category-medico',
        buttonClass: 'btn-medico',
      },
      {
        category: 'LAWYER',
        label: 'Legale',
        icon: 'pi-briefcase',
        colorClass: 'category-legale',
        buttonClass: 'btn-legale',
      },
      {
        category: 'BODYSHOP',
        label: 'Carrozzeria',
        icon: 'pi-wrench',
        colorClass: 'category-carrozzeria',
        buttonClass: 'btn-carrozzeria',
      },
      {
        category: 'HAIL_PROVIDER',
        label: 'Provider Grandine',
        icon: 'pi-cloud',
        colorClass: 'category-provider-grandine',
        buttonClass: 'btn-provider-grandine',
      },
      {
        category: 'NETWORK',
        label: 'Network Carrozzerie',
        icon: 'pi-sitemap',
        colorClass: 'category-network-carrozzerie',
        buttonClass: 'btn-network-carrozzerie',
      },
    ];

    // Populate category data from API response
    categories.forEach((cat) => {
      const cardForCategory = cardData.find((c) => c.category === cat.category);
      categoryMap.set(cat.category, {
        ...cat,
        active: cardForCategory?.activeAssets || 0,
        inProgress: cardForCategory?.assetsActive || 0,
        waitingResponse: cardForCategory?.assetsAwaitingResponse || 0,
        contratti: this.activeContractCounts()[cat.category] || 0,
      });
    });

    this.categoryCards.set(Array.from(categoryMap.values()));

    // Initialize charts with the aggregated data
    const categoryTotals = {
      BODYSHOP: 0,
      LAWYER: 0,
      AUTO_EXPERT: 0,
      DOCTOR: 0,
      RE_EXPERT: 0,
      INVESTIGATOR: 0,
      HAIL_PROVIDER: 0,
      NETWORK: 0,
    };

    cardData.forEach((card) => {
      if (categoryTotals.hasOwnProperty(card.category)) {
        categoryTotals[card.category as keyof typeof categoryTotals] = card.activeAssets || 0;
      }
    });

    this.initializeCharts(categoryTotals, categoryTotals);
  }

  /**
   * Loads active professional contracts for the contract cards.
   */
  private loadActiveContractCounts(): void {
    this.contractRegistryService.getAllContracts().subscribe({
      next: (response) => {
        const contracts = response?.status && Array.isArray(response?.data?.content)
          ? response.data.content
          : [];
        const counts: Record<string, number> = {};

        contracts.forEach((contract: any) => {
          if (String(contract.status).toUpperCase() !== 'ACTIVE') {
            return;
          }

          const category = contract.professionalType || contract.professional?.type;
          if (category && String(category).toUpperCase().trim() !== 'NK_BODYSHOP') {
            counts[category] = (counts[category] || 0) + 1;
          }
        });

        this.activeContractCounts.set(counts);
        this.updateContractCardValues();
      },
      error: (error) => {
        console.error('Error loading active professional contracts:', error);
        this.activeContractCounts.set({});
        this.updateContractCardValues();
      },
    });
  }

  private getTotalActiveContracts(): number {
    return Object.values(this.activeContractCounts()).reduce((sum, count) => sum + count, 0);
  }

  private updateContractCardValues(): void {
    const counts = this.activeContractCounts();
    this.categoryCards.update((cards) =>
      cards.map((card) => ({ ...card, contratti: counts[card.category] || 0 })),
    );
    this.statusCards.update((cards) =>
      cards.map((card) =>
        card.status === 'CONTRATTI' ? { ...card, value: this.getTotalActiveContracts() } : card,
      ),
    );
  }

  /**
   * Calculates statistics for status and category cards
   */
  private calculateStatistics(): void {
    const trustees = this.allData();

    // Calculate status totals
    const activeCount = trustees.filter((t) => t.status === 'ACTIVE').length;
    const inProgressCount = trustees.filter((t) => t.status === 'IN_LAVORAZIONE').length;
    const waitingCount = trustees.filter((t) => t.status === 'IN_ATTESA_DI_RISPOSTA').length;
    const contrattiCount = trustees.filter((t) => t.status === 'CONTRATTI').length;

    this.statusCards.set([
      {
        label: 'Totali Attivi',
        value: activeCount,
        status: 'ACTIVE',
        styleClass: 'status-active',
      },
      {
        label: 'In Lavorazione',
        value: inProgressCount,
        status: 'IN_LAVORAZIONE',
        styleClass: 'status-in-progress',
      },
      {
        label: 'In Attesa di Risposta',
        value: waitingCount,
        status: 'IN_ATTESA_DI_RISPOSTA',
        styleClass: 'status-waiting',
      },
      {
        label: 'Contratti',
        value: contrattiCount,
        status: 'CONTRATTI',
        styleClass: 'status-contratti',
      },
    ]);

    // Calculate category statistics
    const categories = [
      {
        category: 'AUTO_EXPERT',
        label: 'Perito Auto',
        icon: 'pi-car',
        colorClass: 'category-perito-auto',
        buttonClass: 'btn-perito-auto',
      },
      {
        category: 'RE_EXPERT',
        label: 'Perito RE',
        icon: 'pi-building',
        colorClass: 'category-perito-re',
        buttonClass: 'btn-perito-re',
      },
      {
        category: 'INVESTIGATOR',
        label: 'Investigatore',
        icon: 'pi-search',
        colorClass: 'category-investigatore',
        buttonClass: 'btn-investigatore',
      },
      {
        category: 'DOCTOR',
        label: 'Medico',
        icon: 'pi-heart',
        colorClass: 'category-medico',
        buttonClass: 'btn-medico',
      },
      {
        category: 'LAWYER',
        label: 'Legale',
        icon: 'pi-briefcase',
        colorClass: 'category-legale',
        buttonClass: 'btn-legale',
      },
      {
        category: 'BODYSHOP',
        label: 'Carrozzeria',
        icon: 'pi-wrench',
        colorClass: 'category-carrozzeria',
        buttonClass: 'btn-carrozzeria',
      },
      {
        category: 'HAIL_PROVIDER',
        label: 'Provider Grandine',
        icon: 'pi-cloud',
        colorClass: 'category-provider-grandine',
        buttonClass: 'btn-provider-grandine',
      },
      {
        category: 'NETWORK',
        label: 'Network Carrozzerie',
        icon: 'pi-sitemap',
        colorClass: 'category-network-carrozzerie',
        buttonClass: 'btn-network-carrozzerie',
      },
    ];

    const categoryStats = categories.map((cat) => {
      const categoryTrustees = trustees.filter((t) => t.category === cat.category);
      return {
        ...cat,
        active: categoryTrustees.filter((t) => t.status === 'ACTIVE').length,
        inProgress: categoryTrustees.filter((t) => t.status === 'IN_LAVORAZIONE').length,
        waitingResponse: categoryTrustees.filter((t) => t.status === 'IN_ATTESA_DI_RISPOSTA')
          .length,
        contratti: categoryTrustees.filter((t) => t.status === 'CONTRATTI').length,
      };
    });

    this.categoryCards.set(categoryStats);
    this.updateChartData();
  }

  /**
   * Updates chart data based on current trustee data
   */
  private updateChartData(): void {
    const trustees = this.allData();

    // Calculate totals per category
    const categoryTotals = {
      BODYSHOP: trustees.filter((t) => t.category === 'BODYSHOP').length,
      LAWYER: trustees.filter((t) => t.category === 'LAWYER').length,
      AUTO_EXPERT: trustees.filter((t) => t.category === 'AUTO_EXPERT').length,
      DOCTOR: trustees.filter((t) => t.category === 'DOCTOR').length,
      RE_EXPERT: trustees.filter((t) => t.category === 'RE_EXPERT').length,
      INVESTIGATOR: trustees.filter((t) => t.category === 'INVESTIGATOR').length,
      HAIL_PROVIDER: trustees.filter((t) => t.category === 'HAIL_PROVIDER').length,
      NETWORK: trustees.filter((t) => t.category === 'NETWORK').length,
    };

    // Calculate new registrations (you can adjust this logic based on your API data)
    // For now, assuming all current data represents recent registrations
    const categoryRegistrations = {
      BODYSHOP: trustees.filter((t) => t.category === 'BODYSHOP').length,
      LAWYER: trustees.filter((t) => t.category === 'LAWYER').length,
      AUTO_EXPERT: trustees.filter((t) => t.category === 'AUTO_EXPERT').length,
      DOCTOR: trustees.filter((t) => t.category === 'DOCTOR').length,
      RE_EXPERT: trustees.filter((t) => t.category === 'RE_EXPERT').length,
      INVESTIGATOR: trustees.filter((t) => t.category === 'INVESTIGATOR').length,
      HAIL_PROVIDER: trustees.filter((t) => t.category === 'HAIL_PROVIDER').length,
      NETWORK: trustees.filter((t) => t.category === 'NETWORK').length,
    };

    this.initializeCharts(categoryTotals, categoryRegistrations);
  }

  /**
   * Initializes chart data and options with dynamic data
   */
  private initializeCharts(totals: any, registrations: any): void {
    // Check if dark mode is active
    const isDarkMode = document.documentElement.classList.contains('axa-dark-mode');
    const textColor = isDarkMode ? '#ffffff' : '#374151';
    const gridColor = isDarkMode ? 'rgba(154, 163, 187, 0.2)' : 'rgba(0, 0, 0, 0.1)';

    // Distribution Donut Chart Data
    const distributionData = {
      labels: ['Carrozzerie', 'Legali', 'Periti Auto', 'Medici', 'Periti RE', 'Investigatori', 'Provider Grandine', 'Network Carrozzerie'],
      datasets: [
        {
          data: [
            totals.BODYSHOP,
            totals.LAWYER,
            totals.AUTO_EXPERT,
            totals.DOCTOR,
            totals.RE_EXPERT,
            totals.INVESTIGATOR,
            totals.HAIL_PROVIDER,
            totals.NETWORK,
          ],
          backgroundColor: [
            '#60a5fa', // Blue for Carrozzerie
            '#ef4444', // Red for Legali
            '#1e3a8a', // Dark Blue for Periti Auto
            '#a16207', // WCAG 2.1 1.4.3: Changed from #eab308 (1.9:1) to #a16207 (6.1:1 contrast) - Amber-700 for Medici
            '#047857', // WCAG 2.1 1.4.3: Changed from #10b981 (2.2:1) to #047857 (6.0:1 contrast) - Emerald-700 for Periti RE
            '#ea580c', // Orange for Investigatori
            '#7c3aed', // Violet for Provider Grandine
            '#0e7490', // Cyan-700 for Network Carrozzerie
          ],
          borderWidth: 0,
        },
      ],
    };

    const distributionOptions = {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          position: 'bottom',
          labels: {
            usePointStyle: true,
            padding: 15,
            font: {
              size: 12,
            },
            color: textColor,
          },
        },
        tooltip: {
          callbacks: {
            label: function (context: any) {
              const label = context.label || '';
              const value = context.parsed || 0;
              const total = context.dataset.data.reduce((a: number, b: number) => a + b, 0);
              const percentage = ((value / total) * 100).toFixed(1);
              return `${label}: ${value} (${percentage}%)`;
            },
          },
        },
      },
      cutout: '60%',
    };

    this.distributionChartData.set(distributionData);
    this.distributionChartOptions.set(distributionOptions);

    // New Registrations Bar Chart Data
    const registrationsData = {
      labels: ['Carrozzerie', 'Legali', 'Periti Auto', 'Medici', 'Periti RE', 'Investigatori', 'Provider Grandine', 'Network Carrozzerie'],
      datasets: [
        {
          data: [
            registrations.BODYSHOP,
            registrations.LAWYER,
            registrations.AUTO_EXPERT,
            registrations.DOCTOR,
            registrations.RE_EXPERT,
            registrations.INVESTIGATOR,
            registrations.HAIL_PROVIDER,
            registrations.NETWORK,
          ],
          backgroundColor: ['#60a5fa', '#ef4444', '#1e3a8a', '#a16207', '#047857', '#ea580c', '#7c3aed', '#0e7490'],
          borderRadius: 4,
          barThickness: 35,
          categoryPercentage: 0.7,
        },
      ],
    };

    const registrationsOptions = {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          display: false,
        },
        tooltip: {
          callbacks: {
            label: function (context: any) {
              return `Nuove registrazioni: ${context.parsed.y}`;
            },
          },
          titleFont: {
            size: 14,
          },
          bodyFont: {
            size: 14,
          },
        },
      },
      scales: {
        y: {
          beginAtZero: true,
          ticks: {
            stepSize: 5,
            font: {
              size: 14,
            },
            color: textColor,
          },
          grid: {
            display: true,
            drawBorder: false,
            color: gridColor,
          },
        },
        x: {
          grid: {
            display: false,
          },
          ticks: {
            font: {
              size: 11,
            },
            color: textColor,
            maxRotation: 45,
            minRotation: 0,
            autoSkip: false,
          },
        },
      },
    };

    this.registrationsChartData.set(registrationsData);
    this.registrationsChartOptions.set(registrationsOptions);
  }

  /**
   * Loads trustee data from professionals API - used for table only.
   * Dashboard shows only professionals that require a backoffice action.
   */
  private loadTrustees(
    filters: ProfessionalsListFilters = this.DEFAULT_DASHBOARD_FILTERS,
    filterDetail?: string,
    tableInitialFilters: Record<string, any> = { professionalStatus: ['IN_PROGRESS'] },
  ): void {
    this.loading.set(true);
    this.tableInitialFilters.set(tableInitialFilters);

    this.professionalsService.getProfessionals(0, 1000, 'updatedAt,desc', filters).subscribe({
      next: (response) => {
        if (response?.status && Array.isArray(response?.data?.content)) {
          const mappedData = this.mapProfessionalsForTable(response.data.content);
          this.allData.set(mappedData);
          this.data.set(mappedData);
        } else {
          this.allData.set([]);
          this.data.set([]);
        }

        if (filterDetail) {
          this.messageService.add({
            severity: 'info',
            summary: 'Filtro Applicato',
            detail: filterDetail,
            life: 3000,
          });
          this.scrollToTable();
        }

        this.loading.set(false);
      },
      error: (error) => {
        console.error('Error loading professionals:', error);
        this.messageService.add({
          severity: 'error',
          summary: 'Errore',
          detail: 'Impossibile caricare i dati dashboard. Riprova più tardi.',
          life: 5000,
        });
        this.allData.set([]);
        this.data.set([]);
        this.loading.set(false);
      },
    });
  }

  /**
   * Loads dashboard cards data from API for statistics and charts
   */
  private loadDashboardCards(): void {
    this.professionalsService.getDashboardCards().subscribe({
      next: (response) => {
        if (response?.status && Array.isArray(response?.data)) {
          const cardData = response.data;
          this.calculateStatisticsFromCards(cardData);
        } else {
          this.statusCards.set([]);
          this.categoryCards.set([]);
          this.distributionChartData.set(null);
          this.registrationsChartData.set(null);
        }
      },
      error: (error) => {
        console.error('Error loading dashboard cards:', error);
        this.statusCards.set([]);
        this.categoryCards.set([]);
        this.distributionChartData.set(null);
        this.registrationsChartData.set(null);
      },
    });
  }

  /**
   * Maps ProfessionalSummaryResponse array to TrusteeData for table display
   * Uses new lightweight DTO from API v0.0.4
   */
  private mapProfessionalsForTable(rows: ProfessionalSummaryResponse[]): any[] {
    return rows.map((row) => {
      let formattedDate = this.EMPTY_VALUE;
      if (row.createdAt) {
        try {
          const date = new Date(row.createdAt);
          const day = String(date.getDate()).padStart(2, '0');
          const month = String(date.getMonth() + 1).padStart(2, '0');
          const year = date.getFullYear();
          formattedDate = `${day}/${month}/${year}`;
        } catch (e) {
          console.warn('Invalid date format:', row.createdAt);
        }
      }

      let displayName = this.EMPTY_VALUE;
      if (row.businessName) {
        displayName = row.businessName;
      } else if (row.lastName || row.firstName) {
        const parts = [row.lastName, row.firstName].filter(Boolean);
        displayName = parts.join(' ');
      }

      return {
        id: row.id,
        solCode: row.solCode,
        businessName: displayName,
        supplierName: displayName,
        identificationCode: row.identificationCode || this.EMPTY_VALUE,
        type: row.type,
        professionalStatus: row.professionalStatus,
        status: this.mapProfessionalStatusToActionStatus(row.professionalStatus, row.currentStepKey),
        legalOrPhysical: row.legalOrPhysical,
        createdAt: formattedDate,
        currentStepKey: row.currentStepKey,
      };
    });
  }

  /**
   * Maps professionalStatus to generic status for registry-table action button logic
   * - DRAFT → shows edit pencil (for BO modifications)
   * - ACTIVE → shows detail eye (for readonly view)
   */
  /**
   * Step post-conferma MFA: una volta approvati i dati MFA il fiduciario prosegue
   * verso il contratto e non deve più essere modificabile né eliminabile
   * (solo visualizzazione dettaglio), anche se lo stato derivato è ancora IN_PROGRESS.
   */
  private static readonly MFA_CONFIRMED_STEPS = new Set([
    'CONTRACT_SENT',
    'CONTRACT_SIGNATURE',
    'CONTRACT_COUNTERSIGNATURE',
  ]);

  private mapProfessionalStatusToActionStatus(professionalStatus: string, currentStepKey?: string): string {
    // Dati MFA confermati → sola lettura (niente modifica/eliminazione)
    if (currentStepKey && Dashboard.MFA_CONFIRMED_STEPS.has(currentStepKey)) {
      return 'ACTIVE'; // Read-only view
    }

    switch (professionalStatus) {
      case 'IN_PROGRESS':
      case 'AWAITING_RESPONSE':
      case 'SUSPENDED':
        return 'DRAFT'; // Editable by BO
      case 'ACTIVE':
      case 'CEASED':
      default:
        return 'ACTIVE'; // Read-only view
    }
  }

  /**
   * Gets display label for professional status
   */
  private getStatusDisplay(status: string): string {
    const statusMap: Record<string, string> = {
      ACTIVE: 'Attivo',
      IN_PROGRESS: 'In Lavorazione',
      AWAITING_RESPONSE: 'In Attesa Risposta',
      SUSPENDED: 'Sospeso',
      CEASED: 'Cessato',
    };
    return statusMap[status] || status;
  }

  // =====================
  // EVENT HANDLERS
  // =====================

  /**
   * Filters data by status when status card is clicked
   */
  filterByStatus(status: string): void {
    if (status === 'ACTIVE' || status === 'IN_ATTESA_DI_RISPOSTA') {
      this.router.navigate(['/axa', 'professionals-registry'], {
        queryParams: { status }
      });
      return;
    }

    if (status === 'IN_LAVORAZIONE') {
      this.filterInProgressTable();
      return;
    }

    if (status === 'CONTRATTI') {
      this.router.navigate(['/axa', 'professional-contracts-registry'], {
        queryParams: { status: 'ACTIVE' },
      });
      return;
    }

    const filtered = this.allData().filter((t) => t.status === status);
    this.data.set(filtered);

    this.messageService.add({
      severity: 'info',
      summary: 'Filtro Applicato',
      detail: `Visualizzazione fiduciari con stato: ${this.getStatusDisplay(status)}`,
      life: 3000,
    });

    this.scrollToTable();
  }

  filterInProgressTable(category?: string): void {
    this.currentTableFilters = {
      professionalStatus: ['IN_PROGRESS'],
      ...(category ? { type: [category] } : {}),
    };

    this.loadTrustees(
      this.mapTableFiltersToBeFilters(this.currentTableFilters),
      category
        ? `Visualizzazione fiduciari in lavorazione categoria: ${this.getCategoryDisplay(category)}`
        : 'Visualizzazione di tutti i fiduciari in lavorazione presenti in dashboard',
      { ...this.currentTableFilters },
    );
  }

  /**
   * Filters data by category when category card is clicked
   */
  filterByCategory(category: string): void {
    this.router.navigate(['/axa', 'professionals-registry'], {
      queryParams: { category }
    });
  }

  /**
   * Filters data by both category and status
   */
  filterByCategoryAndStatus(category: string, status: string, destination: 'professionals' | 'contracts' = 'professionals'): void {
    if (destination === 'contracts') {
      this.router.navigate(['/axa', 'professional-contracts-registry'], {
        queryParams: { category, status }
      });
    } else {
      this.router.navigate(['/axa', 'professionals-registry'], {
        queryParams: { category, status }
      });
    }
  }

  /**
   * Handles detail click - navigates to review professional page
   */
  onDetailClick(rowData: any): void {
    if (rowData?.id) {
      // Navigate to review professional page (mode auto-determined by current step)
      this.router.navigate(['/axa', 'review-professional', rowData.id]);

      console.log('Navigating to review professional:', rowData);
    }
  }

  /**
   * Handles edit click - navigates to review professional page
   * Same behavior as detail click since mode is determined by current step
   */
  onEditClick(rowData: any): void {
    if (rowData?.id) {
      // Navigate to review professional page (mode auto-determined by current step)
      this.router.navigate(['/axa', 'review-professional', rowData.id]);

      console.log('Navigating to review professional for edit:', rowData);
    }
  }

  /**
   * Handles export functionality
   */
  onExport(): void {
    this.messageService.add({
      severity: 'info',
      summary: 'Export',
      detail: 'Esportazione dati in corso...',
      life: 3000,
    });
    // TODO: Implement actual export functionality
  }

  /**
   * Handles clear filters
   */
  onClearFilters(): void {
    this.currentTableFilters = { professionalStatus: ['IN_PROGRESS'] };
    this.loadTrustees(this.DEFAULT_DASHBOARD_FILTERS, undefined, {
      professionalStatus: ['IN_PROGRESS'],
    });
    this.messageService.add({
      severity: 'info',
      summary: 'Filtri Azzerati',
      detail: 'Tutti i filtri sono stati rimossi',
      life: 2000,
    });
  }

  onTableFilterChange(event: { value: any; field: string; matchMode: string }): void {
    if (event.field === 'professionalStatus') {
      this.currentTableFilters['professionalStatus'] = ['IN_PROGRESS'];
      this.tableInitialFilters.set({
        ...this.currentTableFilters,
        professionalStatus: ['IN_PROGRESS'],
      });
      return;
    }

    if (
      event.value === null ||
      event.value === '' ||
      event.value === undefined ||
      (Array.isArray(event.value) && event.value.length === 0)
    ) {
      delete this.currentTableFilters[event.field];
    } else {
      this.currentTableFilters[event.field] = event.value;
    }

    this.currentTableFilters['professionalStatus'] = ['IN_PROGRESS'];
    const nextFilters = { ...this.currentTableFilters };
    this.tableInitialFilters.set(nextFilters);

    const col = this.columns.find((column) => column.field === event.field);
    if (col?.filterType === 'select' || col?.filterType === 'multiselect') {
      this.loadTrustees(this.mapTableFiltersToBeFilters(nextFilters), undefined, nextFilters);
    } else {
      this.filterSubject.next(nextFilters);
    }
  }

  private mapTableFiltersToBeFilters(tableFilters: Record<string, any>): ProfessionalsListFilters {
    const beFilters: ProfessionalsListFilters = {};
    const toArray = (value: any) => Array.isArray(value) ? value : value ? [value] : [];

    const types = toArray(tableFilters['type']);
    if (types.length) {
      beFilters.type = types as ProfessionalsListFilters['type'];
    }

    const statuses = ['IN_PROGRESS'];
    beFilters.professionalStatus = statuses as ProfessionalsListFilters['professionalStatus'];

    const natures = toArray(tableFilters['legalOrPhysical']);
    if (natures.length) {
      beFilters.legalOrPhysical = natures as ProfessionalsListFilters['legalOrPhysical'];
    }

    if (tableFilters['supplierName']) {
      beFilters.businessName = tableFilters['supplierName'];
    }
    if (tableFilters['identificationCode']) {
      beFilters.identificationCode = tableFilters['identificationCode'];
    }
    if (tableFilters['id']) {
      beFilters.id = tableFilters['id'];
    }

    return beFilters;
  }

  // =====================
  // DISPLAY HELPERS
  // =====================

  /**
   * Scrolls to the registry table section by explicitly scrolling .content-area.
   * Avoids scrollIntoView which can trigger outer-container layout recalculations
   * and cause the flex footer to grow unexpectedly.
   */
  private scrollToTable(): void {
    setTimeout(() => {
      if (!this.registryTableSection) return;
      const el = this.registryTableSection.nativeElement as HTMLElement;
      const scrollContainer = el.closest('.content-area') as HTMLElement | null;
      if (scrollContainer) {
        const offset =
          el.getBoundingClientRect().top -
          scrollContainer.getBoundingClientRect().top +
          scrollContainer.scrollTop;
        scrollContainer.scrollTo({ top: offset, behavior: 'smooth' });
      }
    }, 100);
  }

  /**
   * Gets display label for category
   * Uses dropdown service values when available, falls back to hardcoded mapping
   */
  private getCategoryDisplay(category: string): string {
    return this.formDropdownService.decodeProfessionalTypeSync(category);
  }

  /**
   * Gets route segment for category
   */
  private getCategoryRoute(category: string): string {
    const routeMap: Record<string, string> = {
      AUTO_EXPERT: 'perito-auto',
      RE_EXPERT: 'perito-re',
      INVESTIGATOR: 'investigatore',
      DOCTOR: 'medico',
      LAWYER: 'legale',
      BODYSHOP: 'carrozzeria',
      HAIL_PROVIDER: 'provider-grandine',
      NETWORK: 'network-carrozzerie',
    };
    return routeMap[category] || 'detail';
  }

  /**
   * WCAG 1.3.1: Helper method for chart accessibility
   * Calculates total distribution to compute percentages in text alternative
   */
  getTotalDistribution(): number {
    const chartData = this.distributionChartData();
    if (!chartData?.datasets?.[0]?.data) {
      return 0;
    }
    return chartData.datasets[0].data.reduce((sum: number, value: number) => sum + value, 0);
  }
}
